import React, { useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { puoGestire } from "@/lib/ruoli";
import { useDaCerca } from "@/lib/cerca";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { toast } from "sonner";
import { Plus, Search, Star, Upload, Edit, Trash2, QrCode, Printer, History,
         ArrowUpFromLine, AlertTriangle, Scale } from "lucide-react";
import ImportListinoDialog from "@/components/ImportListinoDialog";
import QRBox from "@/components/QRBox";
import { authFetch } from "@/lib/api";
import { vedeICosti } from "@/lib/costi";

/**
 * Il magazzino dei materiali di cantiere: raccordi, tubi, valvole. Sta a parte
 * dai DPI perche' sono cose diverse - un DPI ha una taglia, una norma CE e si
 * consegna a una persona che firma; un raccordo ha un'unita' di misura e
 * finisce consumato su un cantiere.
 */
export default function MagazzinoMateriali() {
  const { user } = useAuth();
  const navigate = useNavigate();
  // Materiali, rettifiche e import sono `require_gestione`.
  const isAdmin = puoGestire(user);
  const puoMuovere = ["admin", "amministrativo", "operator", "tecnico", "preposto"]
    .includes(user?.role);
  const [materiali, setMateriali] = useState([]);
  const [categorie, setCategorie] = useState({ categorie: [], totale: 0, preferiti: 0 });
  const [daRiordinare, setDaRiordinare] = useState([]);
  const [cerca, setCerca] = useState("");
  const [filtro, setFiltro] = useState("preferiti");
  useDaCerca("cerca", (v) => { setCerca(v); setFiltro("all"); });
  const [showImport, setShowImport] = useState(false);
  const [editing, setEditing] = useState(null);
  const [showForm, setShowForm] = useState(false);
  const [qrDi, setQrDi] = useState(null);
  const [storicoDi, setStoricoDi] = useState(null);
  const [rettificaDi, setRettificaDi] = useState(null);

  const carica = useCallback(async () => {
    const [{ data: m }, { data: c }, { data: r }] = await Promise.all([
      api.get("/materials"),
      api.get("/materials/categories"),
      api.get("/materials/da-riordinare"),
    ]);
    setMateriali(m);
    setCategorie(c);
    setDaRiordinare(r.articoli.map(a => a.id));
    // Aprire sui preferiti quando non ce n'e' nessuno vorrebbe dire mostrare
    // una pagina vuota a chi ha appena importato ottocento articoli.
    if (c.preferiti === 0) setFiltro(f => (f === "preferiti" ? "all" : f));
  }, []);

  useEffect(() => { carica(); }, [carica]);

  const corrisponde = (m) => {
    if (filtro === "all") return true;
    if (filtro === "preferiti") return m.favorite;
    if (filtro === "da-riordinare") return daRiordinare.includes(m.id);
    return m.category === filtro;
  };

  const visibili = materiali.filter(m =>
    corrisponde(m) && m.name.toLowerCase().includes(cerca.toLowerCase()));

  // Quanti finiranno nel PDF: il foglio segue il filtro, non la ricerca
  // scritta qui sopra. Contare "visibili" direbbe un numero che poi non torna
  // con le etichette stampate.
  const daStampare = materiali.filter(corrisponde).length;

  const toggleStella = async (m) => {
    setMateriali(prev => prev.map(x => x.id === m.id ? { ...x, favorite: !x.favorite } : x));
    try {
      await api.post(`/materials/${m.id}/favorite`);
      const { data } = await api.get("/materials/categories");
      setCategorie(data);
    } catch {
      setMateriali(prev => prev.map(x => x.id === m.id ? { ...x, favorite: m.favorite } : x));
      toast.error("Non sono riuscito a salvare il preferito");
    }
  };

  const stampaEtichette = async () => {
    if (filtro === "da-riordinare") {
      toast.error("Per le etichette scegli i preferiti o una categoria");
      return;
    }
    const quali = filtro === "preferiti" ? "?solo_preferiti=true"
                : filtro === "all" ? "" : `?category=${encodeURIComponent(filtro)}`;
    try {
      const r = await authFetch(`/materials/etichette/pdf${quali}`);
      if (!r.ok) throw new Error();
      const b = await r.blob();
      const u = window.URL.createObjectURL(b);
      const a = document.createElement("a");
      a.href = u; a.download = `etichette_${filtro}.pdf`; a.click();
      window.URL.revokeObjectURL(u);
    } catch { toast.error("Errore nella stampa delle etichette"); }
  };

  const salva = async (form) => {
    try {
      if (editing) await api.put(`/materials/${editing.id}`, form);
      else await api.post("/materials", form);
      toast.success(editing ? "Materiale aggiornato" : "Materiale creato");
      setShowForm(false); setEditing(null);
      carica();
    } catch (e) { toast.error(e.response?.data?.detail || "Errore nel salvataggio"); }
  };

  const elimina = async (m) => {
    if (!window.confirm(`Togliere ${m.name} dall'elenco?`)) return;
    try { await api.delete(`/materials/${m.id}`); carica(); }
    catch { toast.error("Errore nella rimozione"); }
  };

  const chip = (attivo, testo, conteggio, onClick, key, extra = null) => (
    <button key={key} type="button" onClick={onClick} data-testid={`filtro-${key}`}
            className={`text-sm px-3 py-1.5 rounded-full border transition-colors flex items-center gap-1.5 ${
              attivo ? "bg-slate-900 border-slate-900 text-white"
                     : "border-slate-200 text-slate-600 hover:bg-slate-50"}`}>
      {extra}{testo} <span className="opacity-60">{conteggio}</span>
    </button>
  );

  return (
    <div className="space-y-6" data-testid="materiali-page">
      <div className="flex items-end justify-between gap-3 flex-wrap">
        <div>
          <div className="text-xs uppercase tracking-[0.2em] text-orange-600 mb-2">Materiali</div>
          <h1 className="font-display text-4xl font-black tracking-tighter">Magazzino materiali</h1>
          <p className="text-sm text-slate-500 mt-1">
            Raccordi, tubi, valvole · separato dal magazzino DPI
          </p>
        </div>
        <div className="flex gap-2 flex-wrap">
          {isAdmin && (
            <>
              {/* Il pulsante dice cosa stampera. Prima portava scritto soltanto
                  Etichette e non si capiva che seguiva il filtro qui sopra,
                  cosi la stampa dei soli preferiti non la trovava nessuno. */}
              <Button variant="outline" className="h-12" onClick={stampaEtichette}
                      disabled={filtro === "da-riordinare" || daStampare === 0}
                      data-testid="stampa-etichette-btn">
                <Printer className="w-4 h-4 mr-2" />
                {filtro === "preferiti" ? `QR dei preferiti (${daStampare})`
                 : filtro === "all" ? `QR di tutti (${daStampare})`
                 : filtro === "da-riordinare" ? "QR"
                 : `QR · ${filtro} (${daStampare})`}
              </Button>
              <Button variant="outline" className="h-12" onClick={() => setShowImport(true)}
                      data-testid="import-listino-btn">
                <Upload className="w-4 h-4 mr-2" /> Importa listino
              </Button>
              <Button variant="outline" className="h-12"
                      onClick={() => { setEditing(null); setShowForm(true); }}
                      data-testid="nuovo-materiale">
                <Plus className="w-4 h-4 mr-2" /> Nuovo materiale
              </Button>
            </>
          )}
          {puoMuovere && (
            <Button className="bg-orange-500 hover:bg-orange-600 text-white h-12"
                    onClick={() => navigate("/movimenti-materiali")}
                    data-testid="vai-ai-movimenti">
              <ArrowUpFromLine className="w-4 h-4 mr-2" /> Carichi e prelievi
            </Button>
          )}
        </div>
      </div>

      <div className="relative">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
        <Input placeholder="Cerca un materiale..." className="pl-9 h-11"
               value={cerca} onChange={(e) => setCerca(e.target.value)}
               data-testid="cerca-materiale" />
      </div>

      <div className="flex flex-wrap gap-1.5">
        <button type="button" onClick={() => setFiltro("preferiti")} data-testid="filtro-preferiti"
                className={`text-sm px-3 py-1.5 rounded-full border transition-colors flex items-center gap-1.5 ${
                  filtro === "preferiti" ? "bg-orange-500 border-orange-500 text-white"
                                         : "border-slate-200 text-slate-600 hover:bg-slate-50"}`}>
          <Star className={`w-3.5 h-3.5 ${filtro === "preferiti" ? "fill-white" : "fill-orange-400 text-orange-400"}`} />
          Preferiti <span className="opacity-70">{categorie.preferiti}</span>
        </button>
        {daRiordinare.length > 0 && (
          <button type="button" onClick={() => setFiltro("da-riordinare")}
                  data-testid="filtro-da-riordinare"
                  className={`text-sm px-3 py-1.5 rounded-full border transition-colors flex items-center gap-1.5 ${
                    filtro === "da-riordinare" ? "bg-red-600 border-red-600 text-white"
                                               : "border-red-200 text-red-700 hover:bg-red-50"}`}>
            <AlertTriangle className="w-3.5 h-3.5" />
            Da riordinare <span className="opacity-70">{daRiordinare.length}</span>
          </button>
        )}
        {chip(filtro === "all", "Tutti", categorie.totale, () => setFiltro("all"), "all")}
        {categorie.categorie.map(c =>
          chip(filtro === c.categoria, c.categoria, c.articoli,
               () => setFiltro(c.categoria), c.categoria))}
      </div>

      {filtro === "preferiti" && categorie.preferiti === 0 && (
        <p className="text-sm text-slate-500">
          Nessun preferito ancora. Metti la stellina sugli articoli che usi davvero,
          mentre fai i carichi: resteranno qui in cima, senza doverli cercare fra gli
          altri {categorie.totale}.
        </p>
      )}

      {categorie.totale === 0 && (
        <Card className="border-slate-200">
          <CardContent className="p-8 text-center text-sm text-slate-500">
            Il magazzino materiali è vuoto. Importa il listino per popolarlo.
          </CardContent>
        </Card>
      )}

      <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-3">
        {visibili.map(m => (
          <Card key={m.id} className="border-slate-200" data-testid={`materiale-${m.id}`}>
            <CardContent className="p-3 flex items-start gap-2">
              <button type="button" onClick={() => toggleStella(m)}
                      title={m.favorite ? "Togli dai preferiti" : "Tieni fra i preferiti"}
                      data-testid={`stella-${m.id}`} className="p-0.5 rounded hover:bg-slate-50 flex-shrink-0">
                <Star className={`w-5 h-5 ${m.favorite ? "fill-orange-400 text-orange-400" : "text-slate-300"}`} />
              </button>
              <div className="flex-1 min-w-0">
                <div className="font-semibold text-sm leading-tight">{m.name}</div>
                <div className="text-[11px] text-slate-500 flex flex-wrap gap-x-2 mt-0.5">
                  <span>{m.category}</span>
                  <span>al {m.unit}</span>
                  {m.price != null && (
                    <span data-testid={`prezzo-${m.id}`}>{m.price.toFixed(2)} €</span>
                  )}
                </div>
                {m.notes && <div className="text-[11px] text-slate-400 truncate">{m.notes}</div>}
              </div>
              <div className="text-right flex-shrink-0">
                <div className={`text-lg font-bold leading-none ${
                  m.stock <= m.min_stock && m.min_stock > 0 ? "text-red-600" : "text-slate-700"}`}>
                  {m.stock}
                </div>
                <div className="text-[10px] text-slate-400">{m.unit}</div>
                <div className="flex gap-1 mt-1 justify-end">
                  <button type="button" onClick={() => setStoricoDi(m)} title="Movimenti dell'articolo"
                          data-testid={`storico-${m.id}`} className="text-slate-400 hover:text-slate-700">
                    <History className="w-3.5 h-3.5" />
                  </button>
                  <button type="button" onClick={() => setQrDi(m)} title="QR dell'articolo"
                          data-testid={`qr-${m.id}`} className="text-slate-400 hover:text-slate-700">
                    <QrCode className="w-3.5 h-3.5" />
                  </button>
                </div>
                {isAdmin && (
                  <div className="flex gap-1 mt-1 justify-end">
                    <button type="button" onClick={() => setRettificaDi(m)}
                            className="text-slate-400 hover:text-slate-700"
                            data-testid={`rettifica-${m.id}`} title="Correggi la giacenza">
                      <Scale className="w-3.5 h-3.5" />
                    </button>
                    <button type="button" onClick={() => { setEditing(m); setShowForm(true); }}
                            className="text-slate-400 hover:text-slate-700" title="Modifica">
                      <Edit className="w-3.5 h-3.5" />
                    </button>
                    <button type="button" onClick={() => elimina(m)}
                            className="text-slate-400 hover:text-red-600" title="Togli">
                      <Trash2 className="w-3.5 h-3.5" />
                    </button>
                  </div>
                )}
              </div>
            </CardContent>
          </Card>
        ))}
      </div>

      {visibili.length === 0 && categorie.totale > 0 && filtro !== "preferiti" && (
        <p className="text-sm text-slate-400 text-center py-6">Nessun materiale corrisponde.</p>
      )}

      {qrDi && (
        <Dialog open onOpenChange={() => setQrDi(null)}>
          <DialogContent className="text-center">
            <DialogHeader><DialogTitle>Etichetta · {qrDi.name}</DialogTitle></DialogHeader>
            <QRBox percorso={`/materials/${qrDi.id}/qrcode`} titolo={qrDi.name}
                   sottotitolo={`${qrDi.category} · al ${qrDi.unit}`}
                   nomeFile={`etichetta_${qrDi.name}`} />
            <p className="text-xs text-slate-400">
              Da ristampare quando l'etichetta sullo scaffale si rovina.
            </p>
          </DialogContent>
        </Dialog>
      )}

      {storicoDi && (
        <StoricoMateriale materiale={storicoDi} onClose={() => setStoricoDi(null)} />
      )}

      {rettificaDi && (
        <RettificaGiacenza materiale={rettificaDi} onClose={() => setRettificaDi(null)}
                           onSalvato={() => { setRettificaDi(null); carica(); }} />
      )}

      {showImport && (
        <ImportListinoDialog onClose={() => setShowImport(false)}
                             onImported={() => { setShowImport(false); carica(); }} />
      )}
      {showForm && (
        <MaterialeForm initial={editing} categorie={categorie.categorie.map(c => c.categoria)}
                       onSubmit={salva} onClose={() => { setShowForm(false); setEditing(null); }} />
      )}
    </div>
  );
}

/** Da dove viene la giacenza che si legge sulla scheda: chi, quando, per dove. */
function StoricoMateriale({ materiale, onClose }) {
  const [movimenti, setMovimenti] = useState(null);

  useEffect(() => {
    api.get(`/materials/movimenti?material_id=${materiale.id}&limit=100`)
      .then(({ data }) => setMovimenti(data))
      .catch(() => setMovimenti([]));
  }, [materiale.id]);

  const quando = (iso) => new Date(iso).toLocaleString("it-IT",
    { day: "2-digit", month: "2-digit", year: "2-digit", hour: "2-digit", minute: "2-digit" });

  const etichetta = (mv) =>
    mv.tipo === "carico" ? (mv.fornitore ? `Carico · ${mv.fornitore}` : "Carico")
    : mv.tipo === "rettifica" ? "Rettifica inventario"
    : mv.destinazione ? `${mv.destinazione.tipo === "mezzo" ? "Mezzo" : "Cantiere"} ${mv.destinazione.nome}`
    : "Prelievo";

  return (
    <Dialog open onOpenChange={onClose}>
      <DialogContent className="max-w-lg max-h-[85vh] overflow-y-auto">
        <DialogHeader><DialogTitle>Movimenti · {materiale.name}</DialogTitle></DialogHeader>
        {movimenti === null && <p className="text-sm text-slate-400">Caricamento…</p>}
        {movimenti?.length === 0 && (
          <p className="text-sm text-slate-500">
            Questo articolo non si è ancora mosso: nessun carico e nessun prelievo.
          </p>
        )}
        <div className="divide-y divide-slate-100">
          {(movimenti || []).map(mv => (
            <div key={mv.id} className="py-2 flex items-start gap-3" data-testid={`mov-${mv.id}`}>
              <div className={`text-sm font-bold w-16 text-right flex-shrink-0 ${
                mv.delta < 0 ? "text-red-600" : "text-emerald-700"}`}>
                {mv.delta > 0 ? "+" : ""}{mv.delta}
              </div>
              <div className="flex-1 min-w-0">
                <div className="text-sm leading-tight">{etichetta(mv)}</div>
                <div className="text-[11px] text-slate-500">
                  {quando(mv.at)} · {mv.user_name || mv.user_email}
                  {mv.ddt ? ` · bolla ${mv.ddt}` : ""}
                </div>
                {mv.note && <div className="text-[11px] text-slate-400">{mv.note}</div>}
              </div>
              <div className="text-[11px] text-slate-400 flex-shrink-0">
                restano {mv.giacenza_dopo}
              </div>
            </div>
          ))}
        </div>
      </DialogContent>
    </Dialog>
  );
}

/**
 * La giacenza contata a mano quando non torna con quella dell'app.
 * Non e' un carico: la merce non e' entrata adesso, si e' solo scoperto che
 * ce n'era piu' o meno di quanto risultava.
 */
function RettificaGiacenza({ materiale, onClose, onSalvato }) {
  const [giacenza, setGiacenza] = useState(String(materiale.stock ?? 0));
  const [note, setNote] = useState("");
  const [busy, setBusy] = useState(false);

  const salva = async (e) => {
    e.preventDefault();
    const valore = parseFloat(String(giacenza).replace(",", "."));
    if (!(valore >= 0)) { toast.error("Scrivi la quantità contata"); return; }
    setBusy(true);
    try {
      await api.post(`/materials/${materiale.id}/inventario`,
                     { giacenza: valore, note: note || null });
      toast.success("Giacenza corretta");
      onSalvato();
    } catch (e2) {
      toast.error(e2.response?.data?.detail || "Errore nella correzione");
    } finally { setBusy(false); }
  };

  return (
    <Dialog open onOpenChange={onClose}>
      <DialogContent className="max-w-md">
        <DialogHeader><DialogTitle>Giacenza contata · {materiale.name}</DialogTitle></DialogHeader>
        <form onSubmit={salva} className="space-y-3">
          <p className="text-xs text-slate-600 bg-slate-50 border border-slate-200 rounded p-2">
            Adesso l'app dice <strong>{materiale.stock} {materiale.unit}</strong>. Scrivi
            quanti ne hai contati davvero sullo scaffale: la differenza resta segnata come
            rettifica, con il tuo nome.
          </p>
          <div>
            <Label>Quantità contata</Label>
            <Input type="number" inputMode="decimal" step="0.01" min="0" value={giacenza}
                   onChange={(e) => setGiacenza(e.target.value)} data-testid="campo-giacenza" />
          </div>
          <div>
            <Label>Motivo</Label>
            <Input value={note} onChange={(e) => setNote(e.target.value)}
                   placeholder="Inventario di fine anno, rottura, ammanco…" />
          </div>
          <DialogFooter>
            <Button type="button" variant="outline" onClick={onClose}>Annulla</Button>
            <Button type="submit" disabled={busy}
                    className="bg-orange-500 hover:bg-orange-600 text-white"
                    data-testid="conferma-rettifica">
              {busy ? "Salvataggio…" : "Correggi"}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}

function MaterialeForm({ initial, categorie, onSubmit, onClose }) {
  const { user } = useAuth();
  const puoVederePrezzi = vedeICosti(user);
  const [form, setForm] = useState(initial || {
    name: "", category: "", unit: "pz", price: null, min_stock: 0,
    favorite: false, notes: null,
  });

  const submit = (e) => { e.preventDefault(); onSubmit(form); };

  return (
    <Dialog open onOpenChange={onClose}>
      <DialogContent className="max-w-lg">
        <DialogHeader>
          <DialogTitle>{initial ? "Modifica materiale" : "Nuovo materiale"}</DialogTitle>
        </DialogHeader>
        <form onSubmit={submit} className="space-y-3">
          <div><Label>Descrizione</Label>
            <Input value={form.name} required data-testid="campo-nome"
                   onChange={(e) => setForm({ ...form, name: e.target.value })} /></div>
          <div><Label>Categoria</Label>
            <Input list="categorie-materiali" value={form.category} required
                   onChange={(e) => setForm({ ...form, category: e.target.value })} />
            <datalist id="categorie-materiali">
              {categorie.map(c => <option key={c} value={c} />)}
            </datalist>
          </div>
          <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
            <div><Label>Unità</Label>
              <Input value={form.unit} placeholder="pz, m, kg" data-testid="campo-unita"
                     onChange={(e) => setForm({ ...form, unit: e.target.value })} /></div>
            {puoVederePrezzi && (
              <div><Label>Prezzo</Label>
                <Input type="number" step="0.01" min="0" value={form.price ?? ""}
                       data-testid="campo-prezzo"
                       onChange={(e) => setForm({ ...form, price: e.target.value === "" ? null : parseFloat(e.target.value) })} /></div>
            )}
            <div><Label>Scorta minima</Label>
              <Input type="number" min="0" value={form.min_stock}
                     onChange={(e) => setForm({ ...form, min_stock: parseInt(e.target.value) || 0 })} /></div>
          </div>
          <div><Label>Note</Label>
            <Input value={form.notes || ""}
                   onChange={(e) => setForm({ ...form, notes: e.target.value || null })} /></div>
          <DialogFooter>
            <Button type="button" variant="outline" onClick={onClose}>Annulla</Button>
            <Button type="submit" className="bg-orange-500 hover:bg-orange-600 text-white">
              Salva
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
